import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaUser, FaPlus, FaStar } from 'react-icons/fa';

const ROLES = [
  'Hero/Protagonist', 'Villain/Antagonist', 'Sidekick', 'Mentor', 
  'Love Interest', 'Comic Relief', 'Guardian', 'Rival', 'Friend', 'Family Member'
];

export default function AddCharacterModal({ isOpen, onClose, onCharacterAdded }) {
  const [character, setCharacter] = useState({ name: '', age: '', gender: '', role: '', appearance: '' });

  const handleSubmit = () => {
    if (character.name && character.age && character.gender && character.role && character.appearance) {
      onCharacterAdded({ ...character, id: Date.now() });
      setCharacter({ name: '', age: '', gender: '', role: '', appearance: '' });
      onClose();
    }
  };

  const canSubmit = character.name && character.age && character.gender && character.role && character.appearance;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">✨ Add New Character</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">👤</div>
            <h3 className="text-2xl font-bold text-amber-800 mb-2">Tell Us About Your Character!</h3>
            <p className="text-amber-600">Add a new character to your scene</p>
          </div>
          
          <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Character Name</label>
                <input
                  type="text"
                  value={character.name}
                  onChange={(e) => setCharacter({...character, name: e.target.value})}
                  placeholder="Enter character name..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Age</label>
                <input
                  type="number"
                  value={character.age}
                  onChange={(e) => setCharacter({...character, age: e.target.value})}
                  placeholder="Enter age..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Gender</label>
                <input
                  type="text"
                  value={character.gender}
                  onChange={(e) => setCharacter({...character, gender: e.target.value})}
                  placeholder="Enter gender..."
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-800 mb-2">Role in Story</label>
                <select
                  value={character.role}
                  onChange={(e) => setCharacter({...character, role: e.target.value})}
                  className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
                >
                  <option value="">Select role...</option>
                  {ROLES.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Appearance field - full width */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-amber-800 mb-2">
                👤 Character Appearance
              </label>
              <textarea
                value={character.appearance}
                onChange={(e) => setCharacter({...character, appearance: e.target.value})}
                placeholder="Describe their appearance: hair color, clothing, distinctive features, etc..."
                rows="3"
                className="w-full p-3 border-2 border-amber-200 rounded-lg focus:border-amber-400 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="p-6 border-t border-amber-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 flex items-center space-x-2"
          >
            <FaPlus />
            <span>Add Character</span>
          </button>
        </div>
      </div>
    </div>
  );
}

AddCharacterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCharacterAdded: PropTypes.func.isRequired
}; 